import { getExpenseTypes as getExpenseTypesApi } from 'api/expense'
import { getOcrBuilders as getOcrBuildersApi } from 'api/ocrBuilders'
import { getApiGatewayStatus, getSystemSettings } from 'api/systemSettings'
import { getUserProfile as getUserProfileApi, UserProfile } from 'api/user'
import { AUTHORITIES, DOC_TYPE, DocType } from 'config'
import { redirect, useRouteLoaderData } from 'react-router-dom'

class UserProfileGlobalClass {
  private _profile?: UserProfile
  private _docTypes?: DocType[]
  set profile(profile: UserProfile) {
    this._profile = profile
    this._docTypes = [
      this.profile.userGroupConfig.useInvoiceOCR ? DOC_TYPE.INVOICE : undefined,
      this.profile.userGroupConfig.useOrderForm
        ? DOC_TYPE.ORDER_FORM
        : undefined,
      this.profile.userGroupConfig.useOCRBuilder
        ? DOC_TYPE.OCR_BUILDER
        : undefined,
      !this.profile.userGroupConfig.useConcur
        ? DOC_TYPE.GENERAL_OCR
        : undefined,
    ].filter((x) => x) as DocType[]
  }
  get profile() {
    return this._profile!
  }
  get docTypes() {
    return this._docTypes!
  }
  get userGroupConfig() {
    return this._profile!.userGroupConfig
  }
  get authority() {
    return this._profile!.authority
  }
  get isAdmin() {
    // システム管理者
    return AUTHORITIES.ADMIN === this.authority
  }
  get isRegister() {
    // 一般ユーザー
    return AUTHORITIES.REGISTER === this.authority
  }
  get isOperator() {
    // 登録担当者
    return AUTHORITIES.OPERATOR === this.authority
  }
  get isManager() {
    // 運用管理者
    return AUTHORITIES.MANAGER === this.authority
  }
  get onlyOrderForm() {
    const userGroupConfig = this.userGroupConfig
    return (
      userGroupConfig.useOrderForm &&
      !userGroupConfig.useInvoiceOCR &&
      !userGroupConfig.useOCRBuilder
    )
  }
}

export type Profile = Omit<UserProfile, 'userGroupConfig'>

export const GLOBAL_PROFILE = new UserProfileGlobalClass()

export default async function rootLoader() {
  // try only one request to check if session is ok.
  // you gonna be redirected to login page if failed
  const apiGatewayStatus = await getApiGatewayStatus()

  const data = {
    apiGatewayStatus,
    systemSettings: await getSystemSettings(),
    profile: await getUserProfileApi(),
    expenseTypes: await getExpenseTypesApi(),
    ocrBuilders: await getOcrBuildersApi(),
  }
  // this is bad
  // but it's too sweat to have thiese in global
  GLOBAL_PROFILE.profile = data.profile
  const userGroupConfig = data.profile.userGroupConfig

  // apiGWOcrInvoiceStatus: created | none | failed
  // created: can use
  // none: not started using
  // failed: error
  // apigwにpostした場合のresponseには ocr_invoice_v2の結果が無い場合があるので
  // statusが存在しない場合 invoice userかの判断が必要
  const apiGWOcrInvoiceStatus =
    data.apiGatewayStatus.find((s) => s.serviceName === 'ocr_invoice_v2')
      ?.status ||
    // 'failed'
    (userGroupConfig.useInvoiceOCR ? 'failed' : 'none')

  const apiGWOrderFormStatus =
    data.apiGatewayStatus.find((s) => s.serviceName === 'ocr_order_form')
      ?.status ||
    // 'failed'
    (userGroupConfig.useOrderForm ? 'failed' : 'none')

  const apiGWOcrGeneralStatus =
    data.apiGatewayStatus.find((s) => s.serviceName === 'ocr_general')
      ?.status || 'failed'

  const apiGWInitialized =
    apiGWOcrInvoiceStatus !== 'failed' &&
    apiGWOrderFormStatus !== 'failed' &&
    (apiGWOcrInvoiceStatus === 'created' || !userGroupConfig.useInvoiceOCR) &&
    (apiGWOrderFormStatus === 'created' || !userGroupConfig.useOrderForm) &&
    apiGWOcrGeneralStatus === 'created'

  // 既存処理: 無限loading
  if (!apiGWInitialized) {
    return redirect(
      '/gwinit' +
        `?g=${apiGWOcrGeneralStatus}` +
        `&i=${apiGWOcrInvoiceStatus}` +
        `&o=${apiGWOrderFormStatus}` +
        `&ui=${userGroupConfig.useInvoiceOCR ? 1 : 0}` +
        `&uc=${userGroupConfig.useConcur ? 1 : 0}` +
        `&uo=${userGroupConfig.useOrderForm ? 1 : 0}`,
    )
  }

  const normalGroups = data.profile.subGroupInfos
    .filter((x) => x.operationFlowType === 'normal')
    .map((x) => x.name)

  const verifyGroups = data.profile.subGroupInfos
    .filter((x) => x.operationFlowType !== 'normal')
    .map((x) => x.name)

  return {
    ...data,
    apiGWOcrInvoiceStatus,
    apiGWOcrGeneralStatus: apiGWOcrGeneralStatus as
      | 'created'
      | 'failed'
      | 'none',
    normalGroups,
    verifyGroups,
    profile: data.profile as Profile,
    userGroupConfig: userGroupConfig,
    expenseTypesOptions:
      data.expenseTypes?.map((t) => ({ label: t.value, value: t.code })) || [],
  }
}

export function useRootLoaderData() {
  return useRouteLoaderData('root') as Exclude<
    Awaited<ReturnType<typeof rootLoader>>,
    Response
  >
}
